import React, { useEffect, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// ForecastStatementMatrix — Item F (traditional Review Template)
//
// A reviewer-grade statement matrix at GL line-item grain, driven by the
// comparison endpoint (`get_comparison(detail='glGroup', grain='monthly')`).
// One instance renders the Income Statement, another the Balance Sheet.
//
// Lines run down the rows (grouped by rollup, with signed subtotals); time
// runs across the columns (fiscal year by default, click to drill to quarters,
// then months). Aggregation is measure-aware and matches the rule `off_trend`
// already applies:
//   • Balance-sheet lines (measure === 'EOP') are point-in-time → the value at
//     the LAST month of the span. They are NEVER summed across months.
//   • Income-statement lines (measure === 'MTD') are flows → summed across the
//     span.
//
// The "spine" value for a period is Actual where actual history exists, then
// the current-cycle Budget over the forecast horizon, then the Prior cycle for
// years beyond the budget horizon (clearly tagged). A comparison-basis toggle
// reframes the same lines against either the Prior Forecast (`prior`) or the
// annualized Actual run-rate ("Actual Trend").
// ---------------------------------------------------------------------------

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const IS_ROLLUPS = [
  'Interest Income', 'Interest Expense', 'Non-Interest Income',
  'Non-Interest Expense', 'Provision for Credit Losses', 'Tax Expense'
];

const periodYear = (period) => Number(period.slice(0, 4));
const periodMonth = (period) => Number(period.slice(5, 7)); // 1-12
const periodQuarter = (period) => Math.floor((periodMonth(period) - 1) / 3) + 1;

const toMap = (points) => {
  const map = {};
  (points || []).forEach((point) => { map[point.period] = point.value; });
  return map;
};

// Format a raw-dollar figure in $MM.
const fmtMM = (value, digits = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return (value / 1e6).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const fmtDelta = (value, digits = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const mm = value / 1e6;
  const sign = mm > 0 ? '+' : mm < 0 ? '−' : '';
  return `${sign}${Math.abs(mm).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

// ---------------------------------------------------------------------------
// Per-line model: resolve the spine + comparison references from the three
// version series. Built once per line group.
// ---------------------------------------------------------------------------
const buildLine = (group, lastActualPeriod) => {
  const actual = toMap(group.series?.actuals);
  const budget = toMap(group.series?.budget);
  const prior = toMap(group.series?.prior);
  const isBalance = group.measure === 'EOP';

  // Spine: actual ≤ last actual, else budget, else prior.
  const spineAt = (period) => {
    if (actual[period] !== undefined && period <= lastActualPeriod) {
      return { value: actual[period], src: 'actual' };
    }
    if (budget[period] !== undefined) return { value: budget[period], src: 'forecast' };
    if (prior[period] !== undefined) return { value: prior[period], src: 'priorForecast' };
    return null;
  };

  // Trailing actual run-rate ("Actual Trend" basis reference).
  const actualPeriods = Object.keys(actual).sort();
  const trailing = actualPeriods.slice(-12);
  const trailingSum = trailing.reduce((sum, period) => sum + actual[period], 0);
  const rrMonthlyFlow = trailing.length ? trailingSum / trailing.length : null;
  const lastActualBalance = actualPeriods.length ? actual[actualPeriods[actualPeriods.length - 1]] : null;

  return {
    glGroup: group.glGroup,
    rollup: group.rollup,
    bsClass: group.bsClass, // Major_BS_Rollup_1 — Assets / Liabilities / Equity (B/S lines)
    measure: group.measure,
    isBalance,
    actual,
    budget,
    prior,
    spineAt,
    rrMonthlyFlow,
    lastActualBalance,

    // Aggregate the spine across a span of month periods.
    aggSpine(periods) {
      if (isBalance) {
        // EOP: value at the latest month in the span that has a spine value.
        for (let i = periods.length - 1; i >= 0; i -= 1) {
          const cell = spineAt(periods[i]);
          if (cell) return cell.value;
        }
        return null;
      }
      let sum = 0;
      let seen = false;
      periods.forEach((period) => {
        const cell = spineAt(period);
        if (cell) { sum += cell.value; seen = true; }
      });
      return seen ? sum : null;
    },

    // Aggregate the prior-forecast series across a span.
    aggPrior(periods) {
      if (isBalance) {
        for (let i = periods.length - 1; i >= 0; i -= 1) {
          if (prior[periods[i]] !== undefined) return prior[periods[i]];
        }
        return null;
      }
      let sum = 0;
      let seen = false;
      periods.forEach((period) => {
        if (prior[period] !== undefined) { sum += prior[period]; seen = true; }
      });
      return seen ? sum : null;
    },

    // Actual-trend reference across a span: flat run-rate carry.
    aggActualTrend(periods) {
      if (isBalance) return lastActualBalance;
      if (rrMonthlyFlow === null) return null;
      return rrMonthlyFlow * periods.length;
    }
  };
};

// Determine the source tag for a span of periods.
const spanSource = (periods, lastActualPeriod, budgetEndPeriod) => {
  let hasActual = false;
  let hasForecast = false;
  let hasPrior = false;
  periods.forEach((period) => {
    if (period <= lastActualPeriod) hasActual = true;
    else if (period <= budgetEndPeriod) hasForecast = true;
    else hasPrior = true;
  });
  if (hasActual && (hasForecast || hasPrior)) return 'mixed';
  if (hasActual) return 'actual';
  if (hasForecast) return 'forecast';
  if (hasPrior) return 'priorForecast';
  return 'forecast';
};

// FY column label suffix by source (mixed gets a leading space: "FY2026 A+F").
const FY_LABEL_SUFFIX = { actual: 'A', mixed: ' A+F', forecast: 'F', priorForecast: 'P' };

// ---------------------------------------------------------------------------
// Row spec — the ordered statement structure with signed subtotals.
// ---------------------------------------------------------------------------
const buildRowSpec = (lines, statement) => {
  const byRollup = (rollup) => lines.filter((line) => line.rollup === rollup);

  if (statement === 'BS') {
    // Group by balance-sheet class (Major_BS_Rollup_1: Assets / Liabilities /
    // Equity) rather than GL-name string matching, so securities, cash,
    // borrowings, equity lines, etc. all land under a labeled section. Anything
    // without a recognized class falls into "Other balances" (e.g. an older API
    // that doesn't yet return bsClass) instead of an unlabeled row.
    const SECTION_ORDER = ['Assets', 'Liabilities', 'Equity'];
    // Drop pre-aggregated "Total ..." rollup rows the dimension carries — we
    // compute section totals ourselves and don't want them double-listed.
    const detail = lines.filter((line) => !/^total\s/i.test(line.glGroup));
    const buckets = {};
    detail.forEach((line) => {
      const cls = SECTION_ORDER.includes(line.bsClass) ? line.bsClass : 'Other';
      (buckets[cls] = buckets[cls] || []).push(line);
    });
    const rows = [];
    [...SECTION_ORDER, 'Other'].forEach((cls) => {
      const secLines = buckets[cls];
      if (!secLines || !secLines.length) return;
      rows.push({ type: 'section', label: cls === 'Other' ? 'Other balances' : cls });
      secLines.forEach((line) => rows.push({ type: 'line', line }));
      if (cls === 'Liabilities') {
        const deposits = secLines.filter((line) => /deposit/i.test(line.glGroup));
        if (deposits.length > 1 && deposits.length < secLines.length) {
          rows.push({ type: 'subtotal', label: 'Total deposits', combine: deposits.map((line) => ({ line, sign: 1 })) });
        }
      }
      rows.push({
        type: 'subtotal',
        label: `Total ${cls === 'Other' ? 'other balances' : cls.toLowerCase()}`,
        combine: secLines.map((line) => ({ line, sign: 1 })),
        strong: true
      });
    });
    return rows;
  }

  // Income statement
  const ii = byRollup('Interest Income');
  const ie = byRollup('Interest Expense');
  const nir = byRollup('Non-Interest Income');
  const nie = byRollup('Non-Interest Expense');
  const prov = byRollup('Provision for Credit Losses');
  const tax = byRollup('Tax Expense');

  const niiCombine = [...ii.map((line) => ({ line, sign: 1 })), ...ie.map((line) => ({ line, sign: -1 }))];
  const nirCombine = nir.map((line) => ({ line, sign: 1 }));
  const nieCombine = nie.map((line) => ({ line, sign: 1 }));
  const ppnrCombine = [...niiCombine, ...nirCombine, ...nie.map((line) => ({ line, sign: -1 }))];
  const pretaxCombine = [...ppnrCombine, ...prov.map((line) => ({ line, sign: -1 }))];
  const niCombine = [...pretaxCombine, ...tax.map((line) => ({ line, sign: -1 }))];

  const rows = [];
  rows.push({ type: 'section', label: 'Net interest income' });
  ii.forEach((line) => rows.push({ type: 'line', line }));
  ie.forEach((line) => rows.push({ type: 'line', line, cost: true }));
  rows.push({ type: 'subtotal', label: 'Net interest income', combine: niiCombine });

  rows.push({ type: 'section', label: 'Noninterest income' });
  nir.forEach((line) => rows.push({ type: 'line', line }));
  rows.push({ type: 'subtotal', label: 'Total noninterest income', combine: nirCombine });

  rows.push({ type: 'section', label: 'Noninterest expense' });
  nie.forEach((line) => rows.push({ type: 'line', line, cost: true }));
  rows.push({ type: 'subtotal', label: 'Total noninterest expense', combine: nieCombine, cost: true });

  rows.push({ type: 'subtotal', label: 'Pre-provision net revenue', combine: ppnrCombine, strong: true });
  prov.forEach((line) => rows.push({ type: 'line', line, cost: true }));
  rows.push({ type: 'subtotal', label: 'Pretax income', combine: pretaxCombine, strong: true });
  tax.forEach((line) => rows.push({ type: 'line', line, cost: true }));
  rows.push({ type: 'subtotal', label: 'Net income', combine: niCombine, strong: true, total: true });

  return rows;
};

// Evaluate a signed combination of lines for a span and reference basis.
const evalCombine = (combine, periods, mode) => {
  let sum = 0;
  let seen = false;
  combine.forEach(({ line, sign }) => {
    const value = mode === 'spine'
      ? line.aggSpine(periods)
      : mode === 'prior'
        ? line.aggPrior(periods)
        : line.aggActualTrend(periods);
    if (value !== null && value !== undefined) { sum += sign * value; seen = true; }
  });
  return seen ? sum : null;
};

// ---------------------------------------------------------------------------
// Column model — fiscal-year / quarter / month with drilldown.
// ---------------------------------------------------------------------------
const buildColumns = (periodsByYear, years, expanded, lastActualPeriod, budgetEndPeriod) => {
  const cols = [];
  years.forEach((year) => {
    const yearPeriods = periodsByYear[year];
    if (!expanded[`Y${year}`]) {
      const src = spanSource(yearPeriods, lastActualPeriod, budgetEndPeriod);
      cols.push({
        id: `Y${year}`, label: `FY${year}${FY_LABEL_SUFFIX[src] || 'F'}`,
        kind: 'Y', year, periods: yearPeriods, src, clickable: true
      });
      return;
    }
    [1, 2, 3, 4].forEach((quarter) => {
      const qPeriods = yearPeriods.filter((period) => periodQuarter(period) === quarter);
      if (!qPeriods.length) return;
      const src = spanSource(qPeriods, lastActualPeriod, budgetEndPeriod);
      if (!expanded[`Q${year}-${quarter}`]) {
        cols.push({
          id: `Q${year}-${quarter}`, label: `${String(year).slice(2)}Q${quarter}`,
          kind: 'Q', year, quarter, periods: qPeriods, src, clickable: true
        });
        return;
      }
      qPeriods.forEach((period) => {
        cols.push({
          id: `M${period}`, label: `${MONTH_SHORT[periodMonth(period) - 1]}-${String(year).slice(2)}`,
          kind: 'M', year, quarter, periods: [period],
          src: spanSource([period], lastActualPeriod, budgetEndPeriod), clickable: true
        });
      });
    });
    const src = spanSource(yearPeriods, lastActualPeriod, budgetEndPeriod);
    cols.push({
      id: `YT${year}`, label: `FY${year} Σ`, kind: 'YT', year, periods: yearPeriods, src, clickable: true
    });
  });
  return cols;
};

const ForecastStatementMatrix = ({ groups, statement, basis, grain }) => {
  const [expanded, setExpanded] = useState({});

  const lines = useMemo(() => {
    const filtered = (groups || []).filter((group) =>
      statement === 'BS' ? group.rollup === 'Balance Sheet' : IS_ROLLUPS.includes(group.rollup)
    );
    // Stable display order.
    const lastActualPeriod = filtered.reduce((max, group) => {
      const periods = (group.series?.actuals || []).map((point) => point.period);
      const localMax = periods.length ? periods[periods.length - 1] : '';
      return localMax > max ? localMax : max;
    }, '');
    return {
      lastActualPeriod,
      built: filtered
        .slice()
        .sort((a, b) => a.glGroup.localeCompare(b.glGroup))
        .map((group) => buildLine(group, lastActualPeriod))
    };
  }, [groups, statement]);

  const { lastActualPeriod, built } = lines;

  const budgetEndPeriod = useMemo(() => {
    let max = '';
    built.forEach((line) => {
      Object.keys(line.budget).forEach((period) => { if (period > max) max = period; });
    });
    return max || lastActualPeriod;
  }, [built, lastActualPeriod]);

  // Union of all month periods across every line + series.
  const { periodsByYear, years } = useMemo(() => {
    const set = new Set();
    built.forEach((line) => {
      [line.actual, line.budget, line.prior].forEach((map) => {
        Object.keys(map).forEach((period) => set.add(period));
      });
    });
    const sorted = [...set].sort();
    const byYear = {};
    sorted.forEach((period) => {
      const year = periodYear(period);
      (byYear[year] = byYear[year] || []).push(period);
    });
    return { periodsByYear: byYear, years: Object.keys(byYear).map(Number).sort((a, b) => a - b) };
  }, [built]);

  // Grain seed: reset the expand state when the page-level grain changes.
  useEffect(() => {
    if (!years.length) return;
    const lastActualYear = lastActualPeriod ? periodYear(lastActualPeriod) : years[years.length - 1];
    if (grain === 'fy') {
      setExpanded({});
    } else if (grain === 'quarter') {
      setExpanded(Object.fromEntries(years.map((year) => [`Y${year}`, true])));
    } else if (grain === 'month') {
      const next = {};
      years.forEach((year) => {
        next[`Y${year}`] = true;
        [1, 2, 3, 4].forEach((quarter) => { next[`Q${year}-${quarter}`] = true; });
      });
      setExpanded(next);
    } else {
      // 'default' — the prior actual year and the current year expanded to
      // quarters; everything older / further out stays as a fiscal-year column.
      setExpanded(Object.fromEntries(
        years
          .filter((year) => year >= lastActualYear - 1 && year <= lastActualYear)
          .map((year) => [`Y${year}`, true])
      ));
    }
  }, [grain, years, lastActualPeriod]);

  const columns = useMemo(
    () => buildColumns(periodsByYear, years, expanded, lastActualPeriod, budgetEndPeriod),
    [periodsByYear, years, expanded, lastActualPeriod, budgetEndPeriod]
  );

  const rowSpec = useMemo(() => buildRowSpec(built, statement), [built, statement]);

  const toggle = (column) => {
    if (column.kind === 'Y') {
      setExpanded((prev) => ({ ...prev, [`Y${column.year}`]: true }));
    } else if (column.kind === 'YT') {
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[`Y${column.year}`];
        [1, 2, 3, 4].forEach((quarter) => delete next[`Q${column.year}-${quarter}`]);
        return next;
      });
    } else if (column.kind === 'Q') {
      setExpanded((prev) => ({ ...prev, [`Q${column.year}-${column.quarter}`]: true }));
    } else if (column.kind === 'M') {
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[`Q${column.year}-${column.quarter}`];
        return next;
      });
    }
  };

  // Resolve a cell value + delta for a row spec entry against a column.
  const cellFor = (row, column) => {
    const { periods } = column;
    let value;
    let reference;
    if (row.type === 'line') {
      value = row.line.aggSpine(periods);
      reference = basis === 'prior' ? row.line.aggPrior(periods) : row.line.aggActualTrend(periods);
    } else if (row.type === 'subtotal') {
      value = evalCombine(row.combine, periods, 'spine');
      reference = evalCombine(row.combine, periods, basis === 'prior' ? 'prior' : 'actualTrend');
    } else {
      return null;
    }
    // Actual-trend basis: only show Δ where the column carries forecast.
    const columnHasForecast = column.src === 'forecast' || column.src === 'mixed' || column.src === 'priorForecast';
    // vs Prior Forecast: show Δ wherever the spine is actual or current-cycle
    // budget (a real comparison). Suppress it on prior-based forward years, where
    // the spine IS the prior series and the variance would be a trivial zero.
    const showDelta = basis === 'prior' ? column.src !== 'priorForecast' : columnHasForecast;
    const delta = (showDelta && value !== null && reference !== null && reference !== undefined)
      ? value - reference
      : null;
    return { value, delta };
  };

  if (!built.length) {
    return <p className="forecast-submit-note">No {statement === 'BS' ? 'balance sheet' : 'income statement'} lines for this scope.</p>;
  }

  const affordance = (column) =>
    column.kind === 'Y' ? '▸' : column.kind === 'Q' ? '▸' : column.kind === 'M' ? '▾' : '✕';

  return (
    <div className="forecast-table-shell ace-scroll-shell forecast-compact-table forecast-statement-shell">
      <table className="forecast-table forecast-time-matrix forecast-statement-matrix">
        <thead>
          <tr>
            <th className="statement-line-col">{statement === 'BS' ? 'Balance sheet line' : 'Income statement line'}</th>
            {columns.map((column) => (
              <th
                key={column.id}
                className={['time-col', column.kind.toLowerCase(), column.src, column.clickable ? 'clickable' : ''].filter(Boolean).join(' ')}
                title={
                  column.kind === 'Y' ? 'Click to drill to quarters'
                    : column.kind === 'Q' ? 'Click to drill to months'
                    : column.kind === 'M' ? 'Click to collapse to quarter'
                    : 'Click to collapse year'
                }
                onClick={() => column.clickable && toggle(column)}
              >
                {column.label}
                <span className="time-col-affordance"> {affordance(column)}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowSpec.map((row, index) => {
            if (row.type === 'section') {
              return (
                <tr key={`s-${index}`} className="statement-section-row">
                  <td colSpan={columns.length + 1}>{row.label}</td>
                </tr>
              );
            }
            const rowClass = row.type === 'subtotal'
              ? `statement-subtotal-row${row.total ? ' statement-total-row' : ''}${row.strong ? ' strong' : ''}`
              : 'statement-line-row';
            return (
              <tr key={`r-${index}`} className={rowClass}>
                <td className="statement-line-col">
                  {row.type === 'subtotal' ? <strong>{row.label}</strong> : row.line.glGroup}
                  {row.type === 'line' && (
                    <small className="forecast-home-market">{row.line.isBalance ? ' EOP' : ' Σ'}{row.cost ? ' · cost' : ''}</small>
                  )}
                </td>
                {columns.map((column) => {
                  const cell = cellFor(row, column);
                  return (
                    <td key={column.id} className={`time-cell ${column.kind.toLowerCase()} ${column.src}`}>
                      <span className="statement-value">{fmtMM(cell?.value)}</span>
                      {cell?.delta !== null && cell?.delta !== undefined && (
                        <span className={`statement-delta ${cell.delta >= 0 ? 'up' : 'down'}`}>
                          {fmtDelta(cell.delta)}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Exported for unit testing — the pure aggregation core (measure-aware EOP vs
// flow dispatch and signed-subtotal evaluation) that guarantees the tie to the
// server pivot.
export { buildLine, buildRowSpec, evalCombine };

export default ForecastStatementMatrix;
